import { type FC, type ReactNode, useCallback, useRef, useState } from "react";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Options for configuring PDF generation behavior
 */
export interface UsePDFGeneratorOptions {
  /**
   * Page orientation
   * @default "portrait"
   */
  orientation?: "portrait" | "landscape";

  /**
   * Page format/size
   * @default "a4"
   */
  format?: "a4" | "letter";

  /**
   * Scale factor for html2canvas rendering (higher = better quality but larger file)
   * @default 2
   */
  scale?: number;
}

/**
 * Return type for the usePDFGenerator hook
 */
export interface UsePDFGeneratorReturn {
  /**
   * Function to generate PDF from the content in PDFPortal.
   * Returns a Promise that resolves with the PDF Blob.
   */
  generatePDF: () => Promise<Blob>;

  /**
   * Whether PDF generation is currently in progress
   */
  isGenerating: boolean;

  /**
   * Error that occurred during PDF generation, if any
   */
  error: Error | null;

  /**
   * Unique identifier generated for this PDF node.
   */
  pdfId: string;

  /**
   * Portal component that renders the PDF content.
   * Content is rendered off-screen for PDF generation.
   * @example
   * ```tsx
   * <PDFPortal>
   *   <YourContentToConvertToPDF />
   * </PDFPortal>
   * ```
   */
  PDFPortal: FC<{ children: ReactNode }>;
}

/**
 * Hook for generating PDFs from React components using html2canvas and jsPDF.
 *
 * This hook provides an API for programmatically generating PDFs by:
 * - Generating a unique ID to avoid conflicts
 * - Rendering content off-screen using React portals
 * - Converting the content to canvas using html2canvas
 * - Generating multi-page PDFs with proper page breaks
 * - Returning the PDF as a Blob for upload or download
 *
 * @param options - Configuration options for PDF generation
 * @returns Object containing generatePDF function, loading/error state, and PDFPortal component
 *
 * @example
 * Basic usage:
 * ```tsx
 * function MyComponent() {
 *   const { generatePDF, isGenerating, PDFPortal } = usePDFGenerator({
 *     filename: "my-document"
 *   });
 *
 *   const handleGenerate = async () => {
 *     const pdfBlob = await generatePDF();
 *     // Upload or download the PDF blob
 *   };
 *
 *   return (
 *     <>
 *       <Button onClick={handleGenerate} disabled={isGenerating}>
 *         {isGenerating ? "Generating..." : "Generate PDF"}
 *       </Button>
 *
 *       <PDFPortal>
 *         <div>Content to convert to PDF</div>
 *       </PDFPortal>
 *     </>
 *   );
 * }
 * ```
 *
 * @example
 * With error handling:
 * ```tsx
 * function DocumentGenerator() {
 *   const { generatePDF, isGenerating, error, PDFPortal } = usePDFGenerator();
 *
 *   const handleGenerate = async () => {
 *     try {
 *       const blob = await generatePDF();
 *       await uploadToServer(blob);
 *     } catch (err) {
 *       console.error("PDF generation failed:", err);
 *     }
 *   };
 *
 *   return (
 *     <>
 *       <Button onClick={handleGenerate} disabled={isGenerating}>
 *         Generate PDF
 *       </Button>
 *       {error && <p>Error: {error.message}</p>}
 *
 *       <PDFPortal>
 *         <FormDocument />
 *       </PDFPortal>
 *     </>
 *   );
 * }
 * ```
 */
export const usePDFGenerator = (options: UsePDFGeneratorOptions = {}): UsePDFGeneratorReturn => {
  const { orientation = "portrait", format = "a4", scale = 2 } = options;

  // Generate a stable unique ID for this PDF node
  const pdfId = useRef(crypto.randomUUID()).current;
  // Direct handle to the portal's content element. Preferred over a document-wide query so
  // generation can't fail to find the node (stale/duplicate ids, portal timing, etc.).
  const contentRef = useRef<HTMLDivElement | null>(null);

  // Track generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Generate PDF from the content in the portal
   */
  const generatePDF = useCallback(async (): Promise<Blob> => {
    setIsGenerating(true);
    setError(null);

    try {
      // Prefer the live ref; fall back to a query for any legacy consumer not using the portal ref.
      const element = contentRef.current ?? (document.querySelector(`[data-pdfid="${pdfId}"]`) as HTMLElement | null);

      if (!element) {
        throw new Error("PDF content element not found");
      }

      // Temporarily make visible by removing clip
      const originalClip = element.style.clip;
      const originalClipPath = element.style.clipPath;
      element.style.clip = "auto";
      element.style.clipPath = "none";

      // Wait for all images to load
      const images = element.querySelectorAll("img");
      await Promise.all(
        Array.from(images).map((img) => {
          if (img.complete) return Promise.resolve();
          return new Promise((resolve, reject) => {
            img.onload = resolve;
            img.onerror = reject;
            // Timeout after 10 seconds
            setTimeout(resolve, 10000);
          });
        })
      );

      // Give browser time to compute all styles
      await new Promise((resolve) => setTimeout(resolve, 100));

      let canvas: HTMLCanvasElement;
      try {
        // Render element to canvas
        canvas = await html2canvas(element, {
          scale,
          useCORS: false, // Disable CORS since we're using data URLs
          allowTaint: true, // Allow tainted canvas - using data URLs from fetch
          logging: false, // Disable logging
          backgroundColor: "#ffffff",
          imageTimeout: 0, // We already waited for images above
          removeContainer: false,
        });
      } catch (error) {
        // Restore clipping on error
        element.style.clip = originalClip;
        element.style.clipPath = originalClipPath;
        throw error;
      } finally {
        // Restore clipping
        element.style.clip = originalClip;
        element.style.clipPath = originalClipPath;
      }

      // Get page dimensions in mm
      const pageWidth = format === "a4" ? 210 : 216; // A4: 210mm, Letter: 216mm (8.5")
      const pageHeight = format === "a4" ? 297 : 279; // A4: 297mm, Letter: 279mm (11")

      // Create PDF document
      const pdf = new jsPDF({
        orientation,
        unit: "mm",
        format,
      });

      // Calculate dimensions for the image
      const imgWidth = orientation === "portrait" ? pageWidth : pageHeight;
      const imgHeight = orientation === "portrait" ? pageHeight : pageWidth;

      // Calculate how many pages we need based on content height
      const canvasWidth = canvas.width;
      const canvasHeight = canvas.height;
      const ratio = canvasHeight / canvasWidth;
      const pdfImgHeight = imgWidth * ratio;

      // If content fits on one page, add it directly
      if (pdfImgHeight <= imgHeight) {
        const imgData = canvas.toDataURL("image/png");
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, pdfImgHeight);
      } else {
        // Multi-page: split content across pages
        const pageCount = Math.ceil(pdfImgHeight / imgHeight);

        for (let i = 0; i < pageCount; i++) {
          if (i > 0) {
            pdf.addPage();
          }

          // Calculate the portion of the canvas for this page
          const srcY = (canvasHeight / pageCount) * i;
          const srcHeight = canvasHeight / pageCount;

          // Create a temporary canvas for this page's content
          const pageCanvas = document.createElement("canvas");
          pageCanvas.width = canvasWidth;
          pageCanvas.height = srcHeight;

          const ctx = pageCanvas.getContext("2d");
          if (!ctx) {
            throw new Error("Failed to get canvas context");
          }

          // Draw the portion of the original canvas for this page
          ctx.drawImage(canvas, 0, srcY, canvasWidth, srcHeight, 0, 0, canvasWidth, srcHeight);

          const pageImgData = pageCanvas.toDataURL("image/png");
          pdf.addImage(pageImgData, "PNG", 0, 0, imgWidth, imgHeight);
        }
      }

      // Generate blob
      const blob = pdf.output("blob");
      setIsGenerating(false);
      return blob;
    } catch (err) {
      const errorObj = err instanceof Error ? err : new Error(String(err));
      setError(errorObj);
      setIsGenerating(false);
      throw errorObj;
    }
  }, [pdfId, scale, orientation, format]);

  // Create the PDFPortal component
  const PDFPortal = useCallback<FC<{ children: ReactNode }>>(
    ({ children }) => {
      // Render on-screen but clipped to be invisible
      // Using clip instead of opacity or positioning ensures CSS is fully computed
      const content = (
        <div
          data-pdfid={pdfId}
          ref={contentRef}
          style={{
            position: "fixed",
            top: "100vh",
            left: 0,
            width: "210mm", // A4 width
            backgroundColor: "white",
            pointerEvents: "none",
            zIndex: -9999,
            clip: "rect(0,0,0,0)", // Hide with clip
            WebkitClipPath: "inset(50%)", // Modern browsers
            clipPath: "inset(50%)", // Hide but still render
          }}
        >
          {children}
        </div>
      );

      return createPortal(content, document.body);
    },
    [pdfId]
  );

  return {
    generatePDF,
    isGenerating,
    error,
    pdfId,
    PDFPortal,
  };
};
